import { videoUrl } from "../../lib/api";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ProductImage } from "../ProductImage";
import { categoryPath } from "../../lib/category-slugs";

/**
 * PackagingShowcase — video loop + grid of packshots.
 * Same progressive strategy as VideoBand: the section starts on the poster,
 * only fetches the video bytes when it's near the viewport, and crossfades
 * poster→video cleanly so the two layers never compound into a ghost.
 */
export const PackagingShowcase = () => {
  const PACK_VIDEO = videoUrl("sauce-pour.mp4");
  const POSTER = videoUrl("immersive-poster.jpg");

  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [failed, setFailed] = useState(false);

  // Stage 1: defer the <video> mount until the section is within ~400px.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    obs.observe(section);
    return () => obs.disconnect();
  }, []);

  // Stage 2: handle play/pause + reveal + iOS gesture retry.
  useEffect(() => {
    if (!shouldLoad) return;
    const video = videoRef.current;
    const section = sectionRef.current;
    if (!video || !section) return;

    if (video.readyState >= 2) setVideoReady(true);
    const onReady = () => setVideoReady(true);
    const onError = () => { setFailed(true); setVideoReady(false); };
    video.addEventListener("canplay", onReady);
    video.addEventListener("loadeddata", onReady);
    video.addEventListener("error", onError);

    const playObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );
    playObs.observe(section);

    const retry = () => { if (video.paused) video.play().catch(() => {}); };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", retry, opts);
    window.addEventListener("scroll", retry, opts);

    return () => {
      playObs.disconnect();
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("error", onError);
      window.removeEventListener("touchstart", retry);
      window.removeEventListener("scroll", retry);
    };
  }, [shouldLoad]);

  // Each packshot gets a descriptive alt so the showcase grid carries
  // real semantic value — was `alt=""` (treated as decorative), which
  // hid 4 distinct emballages alimentaires professionnels from search
  // engines and a11y readers.
  const packshots = [
    { src: "/api/static/products/pochettes-a-kebab.png", alt: "Pochette à kebab — emballage alimentaire professionnel" },
    { src: "/api/static/products/carton-pizza.png", alt: "Carton à pizza pro — emballage restauration rapide" },
    { src: "/api/static/products/sac-kraft-sos.png", alt: "Sac kraft SOS — emballage take-away professionnel" },
    { src: "/api/static/products/box-sandwich.png", alt: "Box sandwich — emballage alimentaire fast-food" },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="packaging-showcase"
      className="relative bg-fd-bg border-t border-fd-border py-24 lg:py-32 px-6 md:px-10 lg:px-14 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Left: hero video */}
        <div
          className="lg:col-span-7 relative aspect-[4/3] overflow-hidden bg-fd-elevated"
          style={{
            backgroundImage: `url(${POSTER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Poster XOR video — poster carries the visual until the video can play, then fades cleanly. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url(${POSTER})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: videoReady && !failed ? 0 : 1,
              transition: "opacity 0.6s ease-out",
            }}
          />
          {shouldLoad && (
            <video
              ref={videoRef}
              src={PACK_VIDEO}
              poster={POSTER}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: videoReady && !failed ? 1 : 0,
                transition: "opacity 0.6s ease-out",
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-fd-bg/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-6 left-6 right-6">
            <div className="mono-label-gold mb-2">Packaging · Service</div>
            <h3 className="font-display text-2xl md:text-4xl uppercase tracking-tight leading-tight">
              Le détail qui fait pro.
            </h3>
          </div>
        </div>

        {/* Right: text + grid */}
        <div className="lg:col-span-5 flex flex-col gap-8">
          <div>
            <div className="mono-label-gold mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-fd-gold"></span>
              <span>Emballage & Service</span>
            </div>
            <h2 className="display-xl text-3xl md:text-5xl lg:text-6xl text-fd-text">
              Une bonne frite <br />
              <span className="text-gradient-gold">mérite un bon contenant.</span>
            </h2>
            <p className="mt-5 font-body text-fd-text/55 text-base">
              Barquettes, gobelets, sacs kraft, couverts, boîtes burger, pots à sauce. Pensé pour le take-away, le drive et la livraison.
            </p>
            <Link to={categoryPath("emballages")} data-cursor-hover className="btn-tertiary mt-6">
              Équiper mon comptoir →
            </Link>
          </div>

          {/* 2x2 packshot grid */}
          <div className="grid grid-cols-2 gap-2">
            {packshots.map((p, i) => (
              <motion.div
                key={p.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="aspect-square fd-stage relative overflow-hidden"
              >
                <ProductImage
                  path={p.src}
                  alt={p.alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-contain p-4"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
